import React from 'react';
import { Menu, Flame, Crown, TrendingUp } from 'lucide-react';
import { gradients } from '../config/theme';
import { getTimeGreeting } from '../utils/dateHelpers';

const Header = ({ 
  user,
  userProfile,
  isPremium = false,
  streakCount = 0,
  onMenuClick,
  onProgressClick,
  subtitle,
  showProgress = false,
  sessionsText
}) => {
  const getUserName = () => {
    if (userProfile?.full_name) return userProfile.full_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    return 'Friend';
  };

  const getUserInitial = () => getUserName().charAt(0).toUpperCase();
  const isAuthenticated = !!user;

  return (
    <header style={{
      background: 'rgba(247,244,240,0.92)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(237,231,220,0.7)',
      position: 'sticky',
      top: 0,
      zIndex: 20,
    }}>
      <div style={{ padding: '10px 14px' }} className="header-inner">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

          {/* ── Logo ──
              390px → 44px | 430px → ~47px | desktop → 72px cap */}
          <img
            src={`${process.env.PUBLIC_URL}/manifest_logo.png`}
            alt="the Daily MANIFEST"
            style={{
              height: 'clamp(44px, 11vw, 72px)',
              maxWidth: 155,
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />

          {/* ── Divider ── */}
          {isAuthenticated && (
            <div style={{
              width: 1, height: 30,
              background: 'rgba(237,231,220,0.9)',
              flexShrink: 0,
            }} />
          )}

          {/* ── Spacer ── */}
          <div style={{ flex: 1 }} />

          {/* ── RIGHT BLOCK ── */}
          {isAuthenticated && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

              {/* Greeting + Progress stacked */}
              <div style={{ textAlign: 'right' }}>

                {/* Greeting row */}
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'flex-end', gap: 5,
                }}>
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#2B2420',
                    whiteSpace: 'nowrap',
                  }}>
                    {getTimeGreeting()}, {getUserName()}
                  </span>
                  {isPremium && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2,
                      background: 'linear-gradient(135deg, #A8856A 0%, #8B6A50 100%)',
                      color: '#FFFFFF',
                      fontSize: 7, fontWeight: 600,
                      padding: '2px 5px', borderRadius: 999,
                      flexShrink: 0, letterSpacing: '0.04em',
                    }}>
                      <Crown style={{ width: 7, height: 7 }} />
                      PRO
                    </span>
                  )}
                </div>

                {/* Progress link */}
                {showProgress && onProgressClick && (
                  <button
                    onClick={onProgressClick}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      marginTop: 2,
                      background: 'none', border: 'none', padding: 0,
                      cursor: 'pointer', color: '#A8856A',
                      minHeight: 'unset', minWidth: 'unset',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#8B6A50'}
                    onMouseLeave={e => e.currentTarget.style.color = '#A8856A'}
                  >
                    <TrendingUp style={{ width: 11, height: 11, flexShrink: 0 }} />
                    {/* Text hidden on ≤409px via style tag below */}
                    <span className="header-progress-label" style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 11, fontWeight: 500,
                      letterSpacing: '0.01em', whiteSpace: 'nowrap',
                    }}>
                      View Your Progress
                    </span>
                  </button>
                )}
              </div>

              {/* Divider before icons */}
              <div style={{
                width: 1, height: 28,
                background: 'rgba(237,231,220,0.9)',
                flexShrink: 0,
              }} />

              {/* Streak pill */}
              {streakCount > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  background: '#FAF8F5', border: '1px solid #E8DDD0',
                  borderRadius: 999, padding: '3px 8px', flexShrink: 0,
                }}>
                  <Flame style={{ width: 12, height: 12, color: '#A8856A' }} />
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 11, fontWeight: 600, color: '#8B6A50',
                  }}>
                    {streakCount}
                  </span>
                </div>
              )}

              {/* Avatar */}
              {userProfile?.profile_photo ? (
                <img
                  src={userProfile.profile_photo}
                  alt="Profile"
                  style={{
                    width: 32, height: 32, borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #FFFFFF',
                    boxShadow: '0 0 0 2px #DDD4C5',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #C0A485 0%, #6F876F 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#FFFFFF',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600, fontSize: 12,
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}>
                  {getUserInitial()}
                </div>
              )}

              {/* Menu — minWidth/minHeight override the global 44px CSS rule */}
              <button
                onClick={onMenuClick}
                aria-label="Menu"
                style={{
                  width: 34, height: 34,
                  minWidth: 34, minHeight: 34,
                  borderRadius: '50%',
                  background: '#F4F0EA',
                  border: 'none', padding: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#EDE7DC'}
                onMouseLeave={e => e.currentTarget.style.background = '#F4F0EA'}
              >
                <Menu style={{ width: 15, height: 15, color: '#9C9080' }} />
              </button>

            </div>
          )}

          {/* Not authenticated — menu only */}
          {!isAuthenticated && (
            <button
              onClick={onMenuClick}
              aria-label="Menu"
              style={{
                width: 34, height: 34,
                minWidth: 34, minHeight: 34,
                borderRadius: '50%',
                background: '#F4F0EA',
                border: 'none', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              <Menu style={{ width: 15, height: 15, color: '#9C9080' }} />
            </button>
          )}

        </div>
      </div>

      {/* ── Responsive label hiding ──────────────────────────────────
          ≤ 409px  (iPhone 12, SE, small Androids):
            "View Your Progress" text hidden — icon only, saves ~105px
          ≥ 410px  (iPhone 14 Pro Max, most modern phones + desktop):
            Full label visible                                         */}
      <style>{`
        @media (max-width: 409px) {
          .header-progress-label { display: none !important; }
        }
        @media (min-width: 768px) {
          .header-inner {
            max-width: 680px;
            margin: 0 auto;
            padding-left: 20px !important;
            padding-right: 20px !important;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;